/**
 * gizmoScaleFeel — how far a scale drag actually scales (ENG-895 follow-up).
 *
 * THE PROBLEM. three's TransformControls derives a scale drag as a RATIO of
 * distances from the gizmo origin:
 *
 *     d = |pointEnd| / |pointStart|
 *
 * The divisor is how far from the centre you happened to grab, so sensitivity
 * is 1/grabDistance — unbounded as you approach the origin. The centre box (the
 * obvious target for uniform scale) sits AT the origin, which is the worst case:
 * measured, one short drag took scale 1 → 5.0 there, while the same drag on the
 * Y handle gave 1 → 1.9. Fine-tuning is impossible and the two handles disagree.
 *
 * THE FIX. Ignore the ratio and derive the factor from RADIAL DRAG DISTANCE,
 * normalised by the gizmo's own on-screen size:
 *
 *     t      = (|pointEnd| - |pointStart|) / gizmoSize
 *     factor = exp(t * rate)
 *
 * Three properties this buys:
 *   - grab-position independent: the divisor is the gizmo's size, not where you
 *     grabbed, so every handle feels identical;
 *   - camera-distance independent: gizmoSize is the same constant-screen-size
 *     factor three uses for the handles, so a 100px drag means the same thing
 *     whether you're 1m or 50m away;
 *   - exponential, so it's symmetric in log space — dragging out then back the
 *     same distance returns exactly to the starting scale, and there's no
 *     asymptote at zero.
 *
 * `fine` (held Shift) shrinks the rate for precision work, the convention in
 * every DCC tool.
 */

// Chosen empirically in the browser: a comfortable ~1 gizmo-width drag lands
// near 2x, and small nudges resolve to a few percent.
export const SCALE_RATE = 0.9

// Shift-drag precision multiplier.
export const FINE_RATE_MULTIPLIER = 0.2

/**
 * three's constant-screen-size factor for the gizmo, mirroring
 * TransformControlsGizmo.updateMatrixWorld so the normaliser matches what the
 * user actually sees. `size` is TransformControls.size (default 1).
 */
export function gizmoWorldSize({ cameraDistance, fovDeg, zoom = 1, size = 1, orthoHeight = null }) {
  if (orthoHeight != null) {
    // Orthographic: three uses (top - bottom) / zoom; the caller passes it in.
    return Math.max(1e-6, (orthoHeight / (zoom || 1)) * size / 4)
  }
  const d = Number(cameraDistance)
  const f = Number(fovDeg)
  if (!Number.isFinite(d) || !Number.isFinite(f) || d <= 0) return null
  const factor = d * Math.min(1.9 * Math.tan(Math.PI * f / 360) / (zoom || 1), 7)
  return Math.max(1e-6, factor * size / 4)
}

/**
 * Uniform scale for the current drag position.
 *
 * @param startScale scale when the drag began (must be > 0)
 * @param r0         |pointStart| — world distance from the gizmo origin at grab
 * @param r1         |pointEnd|   — the same, now
 * @param gizmoSize  from gizmoWorldSize(); null/invalid → returns startScale
 * @param fine       Shift held: finer control
 * @returns the new uniform scale, or startScale when the inputs can't be used
 */
export function uniformScaleFromDrag({ startScale, r0, r1, gizmoSize, fine = false, rate = SCALE_RATE }) {
  const s0 = Number(startScale)
  if (!Number.isFinite(s0) || s0 <= 0) return startScale
  const a = Number(r0), b = Number(r1), g = Number(gizmoSize)
  if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(g) || g <= 0) return s0

  const t = (b - a) / g
  const k = rate * (fine ? FINE_RATE_MULTIPLIER : 1)
  const next = s0 * Math.exp(t * k)
  return Number.isFinite(next) ? next : s0
}
